import { useEffect, useState } from 'react'
import { MapContainer, TileLayer, CircleMarker, useMapEvents } from 'react-leaflet'
import { toast } from 'sonner'
import { Radio, Plus, MapPin, CheckCircle, Navigation } from 'lucide-react'
import {
    dispatchService, Incident, SuggestedUnit, Assignment, IncidentPriority,
} from '@/services/dispatchService'

/** Captura un click en el mapa y lo entrega como punto del incidente. */
function PickPoint({ onPick }: { onPick: (lat: number, lon: number) => void }) {
    useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) })
    return null
}

const PRIORITY_COLOR: Record<IncidentPriority, string> = {
    LOW: 'text-gray-400', MEDIUM: 'text-blue-400', HIGH: 'text-amber-400', CRITICAL: 'text-red-400',
}
const STATUS_COLOR: Record<string, string> = {
    OPEN: 'bg-red-500/20 text-red-300', ASSIGNED: 'bg-amber-500/20 text-amber-300',
    ENROUTE: 'bg-blue-500/20 text-blue-300', ONSCENE: 'bg-emerald-500/20 text-emerald-300',
    RESOLVED: 'bg-gray-500/20 text-gray-400',
}

export default function DispatchPage() {
    const [incidents, setIncidents] = useState<Incident[]>([])
    const [selected, setSelected] = useState<Incident | null>(null)
    const [nearest, setNearest] = useState<SuggestedUnit[]>([])
    const [assignments, setAssignments] = useState<Assignment[]>([])
    const [form, setForm] = useState({ title: '', priority: 'MEDIUM' as IncidentPriority, lat: '', lon: '' })

    const load = () => {
        dispatchService.listActive().then(setIncidents).catch(() => toast.error('No se pudieron cargar los incidentes'))
    }
    useEffect(load, [])

    const select = async (inc: Incident) => {
        setSelected(inc)
        try {
            const [n, a] = await Promise.all([dispatchService.nearest(inc.id), dispatchService.assignments(inc.id)])
            setNearest(n); setAssignments(a)
        } catch { setNearest([]); setAssignments([]) }
    }

    const create = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.title.trim()) return toast.error('Indica un título')
        try {
            await dispatchService.create({
                title: form.title, priority: form.priority,
                lat: form.lat ? parseFloat(form.lat) : undefined,
                lon: form.lon ? parseFloat(form.lon) : undefined,
            })
            toast.success('Incidente creado'); setForm({ ...form, title: '' }); load()
        } catch { toast.error('Error al crear el incidente') }
    }

    const assign = async (userId: string) => {
        if (!selected) return
        try { await dispatchService.assign(selected.id, userId); toast.success('Unidad asignada'); select(selected); load() }
        catch { toast.error('No se pudo asignar') }
    }

    const resolve = async (inc: Incident) => {
        try { await dispatchService.resolve(inc.id); toast.success('Incidente resuelto'); setSelected(null); load() }
        catch { toast.error('No se pudo resolver') }
    }

    return (
        <div className="p-6 text-gray-100">
            <div className="flex items-center gap-3 mb-6">
                <Radio className="text-indigo-400" /><h1 className="text-2xl font-bold">Despacho</h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Cola de incidentes */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
                    <h2 className="font-semibold mb-3">Incidentes activos ({incidents.length})</h2>
                    <div className="space-y-2">
                        {incidents.map((i) => (
                            <button key={i.id} onClick={() => select(i)}
                                className={`w-full text-left bg-gray-900/50 hover:bg-gray-900 rounded px-3 py-2 text-sm ${selected?.id === i.id ? 'ring-1 ring-indigo-500' : ''}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">{i.title}</span>
                                    <span className={`text-xs ${PRIORITY_COLOR[i.priority]}`}>{i.priority}</span>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[i.status] || ''}`}>{i.status}</span>
                            </button>
                        ))}
                        {incidents.length === 0 && <p className="text-gray-500 text-sm">Sin incidentes activos.</p>}
                    </div>
                </div>

                {/* Detalle + despacho */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
                    <h2 className="font-semibold mb-3 flex items-center gap-2"><Navigation size={16} /> Despacho</h2>
                    {!selected && <p className="text-gray-500 text-sm">Selecciona un incidente.</p>}
                    {selected && (
                        <>
                            <div className="mb-3">
                                <div className="font-medium">{selected.title}</div>
                                <button onClick={() => resolve(selected)} className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
                                    <CheckCircle size={14} /> Resolver
                                </button>
                            </div>
                            <h3 className="text-sm text-gray-400 mb-2">Unidades más cercanas</h3>
                            <div className="space-y-2">
                                {nearest.map((u) => (
                                    <div key={u.userId} className="flex items-center justify-between bg-gray-900/50 rounded px-3 py-2 text-sm">
                                        <span>{u.callsign || u.userId.slice(0, 8)} · {Math.round(u.distanceMeters)} m · ETA {Math.round(u.etaSeconds / 60)}′</span>
                                        <button onClick={() => assign(u.userId)} className="text-indigo-400 hover:text-indigo-300 text-xs">Asignar</button>
                                    </div>
                                ))}
                                {nearest.length === 0 && <p className="text-gray-500 text-xs">Sin unidades activas cerca.</p>}
                            </div>
                            {assignments.length > 0 && (
                                <div className="mt-3">
                                    <h3 className="text-sm text-gray-400 mb-1">Asignadas</h3>
                                    {assignments.map((a) => (
                                        <div key={a.id} className="text-xs text-gray-300">{a.callsign || a.userId.slice(0, 8)} · {a.status}</div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Crear incidente */}
                <form onSubmit={create} className="bg-gray-800/40 border border-gray-700 rounded-lg p-4 space-y-3 h-fit">
                    <h2 className="font-semibold flex items-center gap-2"><Plus size={16} /> Nuevo incidente</h2>
                    <input className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                        placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <select className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm"
                        value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as IncidentPriority })}>
                        {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <div className="rounded overflow-hidden border border-gray-700" style={{ height: 160 }}>
                        <MapContainer center={[-33.4489, -70.6693]} zoom={12} style={{ height: '100%', width: '100%', background: '#1a1a1a' }}>
                            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                            <PickPoint onPick={(lat, lon) => setForm({ ...form, lat: lat.toFixed(6), lon: lon.toFixed(6) })} />
                            {form.lat && form.lon && (
                                <CircleMarker center={[parseFloat(form.lat), parseFloat(form.lon)]} radius={7}
                                    pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.8 }} />
                            )}
                        </MapContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm" placeholder="Lat"
                            value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
                        <input className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm" placeholder="Lon"
                            value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })} />
                    </div>
                    <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> Clic en el mapa fija la ubicación y habilita "más cercano"</p>
                    <button className="w-full bg-indigo-600 hover:bg-indigo-500 rounded px-3 py-2 text-sm font-medium">Crear</button>
                </form>
            </div>
        </div>
    )
}
